import React from 'react';
import Table, { CellProps } from 'rsuite/Table';
import IconButton from 'rsuite/IconButton';
import useSWR from 'swr';
import { trpc, RouterOutput } from '../../../../api/trpc';
import { RbacObject } from '../../../../export-types/cleaned-types';
import { useRbac } from '../../../common/Rbac';
import { ModalsService } from '../../../../services/modals.service';
import { FeatureFlagEditModal } from './feature-flag-edit-modal';
import { FEATURE_FLAG_LABELS } from '../../../../consts/feature-flags';
import moment from 'moment';

type FlagItem = RouterOutput['featureFlag']['list'][number];

export const FeatureFlagsTab = () => {
  const { data, isLoading, mutate } = useSWR(['featureFlag.list'], () =>
    trpc.featureFlag.list.query(),
  );
  const { hasPermission, isLoading: isRbacLoading } = useRbac();
  const canEditFeatureFlags =
    !isRbacLoading && hasPermission(RbacObject.FeatureFlag, 'write:admin');

  const ActionCell = ({ rowData, ...props }: CellProps<FlagItem>) => {
    if (!rowData) return null;
    if (isRbacLoading) return null;

    return (
      <Table.Cell
        {...props}
        className="link-group no-padding-cell pt-2"
        align="right"
      >
        <IconButton
          appearance="subtle"
          title={canEditFeatureFlags ? 'Редактировать' : 'Посмотреть'}
          icon={
            <i className={canEditFeatureFlags ? 'bi bi-pencil' : 'bi bi-eye'} />
          }
          onClick={() =>
            ModalsService.createModal(FeatureFlagEditModal, {
              flag: rowData,
              mutate,
              readOnly: !canEditFeatureFlags,
            })
          }
        />
      </Table.Cell>
    );
  };

  const NameCell = ({ rowData, ...props }: CellProps<FlagItem>) => {
    if (!rowData) return null;
    return (
      <Table.Cell {...props}>
        {FEATURE_FLAG_LABELS[rowData.name] ?? rowData.name}
      </Table.Cell>
    );
  };

  const EnabledForAllCell = ({ rowData, ...props }: CellProps<FlagItem>) => {
    if (!rowData) return null;
    return (
      <Table.Cell {...props}>{rowData.enabledForAll ? 'Да' : 'Нет'}</Table.Cell>
    );
  };

  const DateCell = ({ rowData, dataKey, ...props }: CellProps<FlagItem>) => {
    if (!rowData) return null;
    const date =
      dataKey === 'createdAt' ? rowData.createdAt : rowData.updatedAt;
    const isInitialUpdate =
      dataKey === 'updatedAt' &&
      moment(rowData.updatedAt).isSame(rowData.createdAt);
    return (
      <Table.Cell {...props}>
        {date && !isInitialUpdate
          ? moment(date).format('DD.MM.YYYY HH:mm')
          : '—'}
      </Table.Cell>
    );
  };

  const UsersCountCell = ({ rowData, ...props }: CellProps<FlagItem>) => {
    if (!rowData) return null;
    return (
      <Table.Cell {...props}>
        {rowData.enabledForAll ? '—' : rowData.users.length}
      </Table.Cell>
    );
  };

  return (
    <Table data={data ?? []} headerHeight={57} autoHeight loading={isLoading}>
      <Table.Column flexGrow={2} fullText>
        <Table.HeaderCell>Название</Table.HeaderCell>
        <NameCell dataKey="name" />
      </Table.Column>
      <Table.Column flexGrow={1}>
        <Table.HeaderCell>Для всех</Table.HeaderCell>
        <EnabledForAllCell />
      </Table.Column>
      <Table.Column flexGrow={1}>
        <Table.HeaderCell>Пользователи</Table.HeaderCell>
        <UsersCountCell />
      </Table.Column>
      <Table.Column width={170}>
        <Table.HeaderCell>Дата создания</Table.HeaderCell>
        <DateCell dataKey="createdAt" />
      </Table.Column>
      <Table.Column width={170}>
        <Table.HeaderCell>Дата изменения</Table.HeaderCell>
        <DateCell dataKey="updatedAt" />
      </Table.Column>
      <Table.Column width={60}>
        <Table.HeaderCell> </Table.HeaderCell>
        <ActionCell />
      </Table.Column>
    </Table>
  );
};
